// Turns raw ping/pong round trips into a smoothed RTT/jitter estimate and the tick lead the client should
// run its own simulation ahead of the server by, so the tick a command sent now is tagged with is the tick
// the server will actually apply it on. `leadTicks` only ever sizes THIS client's own replay window (see
// predictedGame.ts); there is no server-side input buffer keyed on a command's tick — match.ts drains
// whatever has arrived at the next tick regardless (see commandChannel.ts's own header on why the standing
// order exists instead). Same smoothing formulas TCP's own retransmission-timeout estimator uses (RFC
// 6298's SRTT/RTTVAR, the Jacobson/Karels algorithm), with three things RFC 6298 doesn't need to worry
// about added on top: a fast warmup (net/connection.ts sends its first few pings quickly) so a handful of
// samples are averaged together before the outlier gate below ever engages; a gate that can let go of a bad
// lock instead of only ever resisting single spikes; and a stall guard (STALL_SAMPLE_MS) rejecting a round
// trip large enough that it almost certainly means THIS client stopped running for a while (backgrounded,
// thrown to sleep, a long GC pause) rather than the network actually taking that long — RFC 6298's
// estimator was designed for a measuring instrument that never itself stops. Unlike an ordinary outlier,
// a stall was let all the way into RTTVAR (which, unlike SRTT, had no protection at all): a single
// multi-hundred-ms stall sample reported ~87ms of "jitter" on an idle, genuinely ~2ms localhost connection.
import { STEP_DT } from '../../core/game.js';

const ALPHA = 1 / 8;        // SRTT smoothing factor (RFC 6298)
const BETA = 1 / 4;         // RTTVAR smoothing factor (RFC 6298)
const OUTLIER_FACTOR = 4;   // a sample this many times the current SRTT still feeds the jitter estimate
                             // (the margin should widen — this CAN happen) but does not drag SRTT itself
const WARMUP_SAMPLES = 3;   // averaged outright, before the outlier gate is trusted to judge anything
const MAX_CONSECUTIVE_OUTLIERS = 3;  // this many genuine samples rejected in a row means the CURRENT
                                       // estimate is the outlier, not the incoming samples
const STALL_SAMPLE_MS = 1000;  // a round trip this large is not usable real-time-gameplay information
                                 // regardless of cause — see this module's own header
const TICK_MS = STEP_DT * 1000;
// The server-tick estimate is filtered, not re-anchored on every pong: each reply carries its own one-way
// jitter, and taking each as the truth moved the estimate by up to ±5 ticks at once every few seconds at
// 200ms/50ms (a visible jump in whatever runs on it). A reading this far off the running estimate is a real
// discontinuity (the server stalled, the client's clock was reset), not jitter, and is taken outright.
const TICK_GAIN = 1 / 4;
const TICK_RESET = 10;

export type ClockSync = {
  /** Feed one round trip. `sentAtMs` is what this client stamped its `ping` with, `nowMs` is when the
      `pong` reply arrived, `serverTick` is the server's own tick count at the moment it replied
      (protocol.ts's `pong.tick`). */
  onPong(sentAtMs: number, nowMs: number, serverTick: number): void;
  /** Smoothed round trip, ms (RFC 6298's SRTT). */
  readonly rttMs: number;
  /** Smoothed mean deviation, ms (RFC 6298's RTTVAR) — the jitter figure. */
  readonly jitterMs: number;
  /** Ticks (fractional) the client should run ahead of the server by: the one-way delay a command sent now
      still has to cover, minus half a tick. The server applies a command on arrival, not on its tagged
      tick, so the best lead is the one that makes the tag match that arrival on AVERAGE: any extra margin
      (this used to add the jitter estimate and round up) tags every order ticks after the server really
      applied it, and the prediction then jumps ahead by that much the moment the ack lands. The half tick
      is the two sides' own rounding: pong.tick is the server's tick counter mid-way through a tick on
      average, and a command takes effect at the next tick boundary on both the client and the server. */
  readonly leadTicks: number;
  /** The server's own tick count right now (fractional), extrapolated from the filtered pong estimate using
      elapsed wall time — pongs land every few seconds, this is what the caller reads on every rendered
      frame in between. */
  estimatedServerTick(nowMs: number): number;
  readonly sampleCount: number;
};

export function createClockSync(): ClockSync {
  let srtt = 0, rttvar = 0, sampleCount = 0;
  let anchorTick = 0, anchorAtMs = 0, tickSamples = 0;
  let consecutiveOutliers = 0;

  return {
    onPong(sentAtMs, nowMs, serverTick) {
      const sample = Math.max(0, nowMs - sentAtMs);
      if (sample > STALL_SAMPLE_MS) {
        // Discarded from SRTT, RTTVAR, sampleCount and the tick estimate alike — see STALL_SAMPLE_MS's own
        // comment. Not folded in as even a rejected-outlier sample (which would still have widened RTTVAR):
        // a stall this large says nothing at all about the network, honest or otherwise, and the reply sat
        // unread for most of it, so its tick reads that much behind the server's.
        return;
      } else if (sampleCount < WARMUP_SAMPLES) {
        // Averaged outright rather than gated: a single cold-start sample (net/connection.ts's very first
        // ping) taken as-is used to become SRTT permanently, with no way back if it happened to be unlucky
        // — a low fluke then rejects every honest sample after it forever (sample <= srtt*4 never holds
        // again once srtt is stuck near 0). A few fast samples averaged together first is cheap insurance.
        rttvar = sampleCount === 0 ? sample / 2 : (1 - BETA) * rttvar + BETA * Math.abs(sample - srtt);
        srtt = sampleCount === 0 ? sample : (srtt + sample) / 2;
        consecutiveOutliers = 0;
        sampleCount++;
      } else if (sample <= srtt * OUTLIER_FACTOR) {
        rttvar = (1 - BETA) * rttvar + BETA * Math.abs(sample - srtt);
        srtt = (1 - ALPHA) * srtt + ALPHA * sample;
        consecutiveOutliers = 0;
        sampleCount++;
      } else {
        // Still feeds the jitter estimate (the margin should widen — this CAN happen) but not SRTT itself
        // — UNLESS this is the latest in a run of several: that many genuine samples in a row all reading
        // as outliers is stronger evidence that SRTT itself is stale than that reality is flaking that
        // consistently, so the gate lets go and re-locks onto what is actually arriving instead of
        // rejecting it forever.
        rttvar = (1 - BETA) * rttvar + BETA * Math.abs(sample - srtt);
        consecutiveOutliers++;
        if (consecutiveOutliers >= MAX_CONSECUTIVE_OUTLIERS) { srtt = sample; consecutiveOutliers = 0; }
        sampleCount++;
      }
      // The tick estimate still benefits from every reply regardless of whether its timing was trusted for
      // RTT/jitter: the server really was at `serverTick` when it answered. The pong's own return trip has
      // already taken about half the round trip by the time it lands here, so the server's CURRENT tick is
      // ahead of what it reported by that much. Warmup samples are taken outright, like SRTT's.
      const reading = serverTick + srtt / 2 / TICK_MS;
      const current = anchorTick + (nowMs - anchorAtMs) / TICK_MS;
      const off = reading - current;
      anchorTick = tickSamples < WARMUP_SAMPLES || Math.abs(off) > TICK_RESET ? reading : current + TICK_GAIN * off;
      anchorAtMs = nowMs;
      tickSamples++;
    },
    get rttMs() { return srtt; },
    get jitterMs() { return rttvar; },
    get leadTicks() {
      if (sampleCount === 0) return 2;  // a small, safe default before the first sample ever lands
      return Math.max(0, srtt / 2 / TICK_MS - 0.5);
    },
    estimatedServerTick(nowMs) {
      if (sampleCount === 0) return 0;
      return anchorTick + (nowMs - anchorAtMs) / TICK_MS;
    },
    get sampleCount() { return sampleCount; },
  };
}
